/**
 * Session manager for MCP transports.
 * Handles creation, retrieval, and cleanup of StreamableHTTPServerTransport instances.
 * Also stores request context (headers) per session for authentication.
 */
import { randomUUID } from 'crypto'

// We'll dynamically import the SDK to avoid hard dependency issues
let StreamableHTTPServerTransport: any = null

/**
 * Request context stored per session.
 * Contains HTTP headers and other request info needed for authentication.
 */
export interface RequestContext {
  headers: Record<string, string | string[] | undefined>
  method?: string
  url?: string
  ip?: string
}

/**
 * Lazily load the MCP SDK transport.
 */
async function getTransportClass(): Promise<any> {
  if (!StreamableHTTPServerTransport) {
    try {
      const module = await import('@modelcontextprotocol/sdk/server/streamableHttp.js')
      StreamableHTTPServerTransport = module.StreamableHTTPServerTransport
    } catch (error) {
      throw new Error(
        'Failed to load @modelcontextprotocol/sdk. Make sure it is installed: npm install @modelcontextprotocol/sdk',
      )
    }
  }
  return StreamableHTTPServerTransport
}

/**
 * Configuration for session manager.
 */
export interface SessionManagerConfig {
  /** Callback when a session is created */
  onSessionCreated?: (sessionId: string) => void
  /** Callback when a session is destroyed */
  onSessionDestroyed?: (sessionId: string) => void
  /** Logger function */
  log?: (message: string) => void
}

/**
 * Manages MCP transport sessions.
 */
export class SessionManager {
  private sessions: Map<string, any> = new Map()
  private requestContexts: Map<string, RequestContext> = new Map()
  private mcpServer: any = null
  private config: SessionManagerConfig

  constructor(config: SessionManagerConfig = {}) {
    this.config = config
  }

  /**
   * Set the MCP server that transports will connect to.
   */
  setMcpServer(server: any): void {
    this.mcpServer = server
  }

  /**
   * Generate a new session ID.
   */
  generateSessionId(): string {
    return randomUUID()
  }

  /**
   * Check if a session exists.
   */
  hasSession(sessionId: string): boolean {
    return this.sessions.has(sessionId)
  }

  /**
   * Get an existing session's transport.
   */
  getSession(sessionId: string): any | undefined {
    return this.sessions.get(sessionId)
  }

  /**
   * Store request context (headers, etc.) for a session.
   * This is called when an HTTP request arrives, before dispatching to MCP.
   */
  setRequestContext(sessionId: string, context: RequestContext): void {
    this.requestContexts.set(sessionId, context)
  }

  /**
   * Get the stored request context for a session.
   * Returns undefined if no context is stored.
   */
  getRequestContext(sessionId: string): RequestContext | undefined {
    return this.requestContexts.get(sessionId)
  }

  /**
   * Get or create a transport for a session.
   */
  async getOrCreateSession(sessionId: string): Promise<any> {
    if (this.sessions.has(sessionId)) {
      const existingSession = this.sessions.get(sessionId)
      if (existingSession) {
        return existingSession
      }
    }

    if (!this.mcpServer) {
      throw new Error('MCP server not set. Call setMcpServer() first.')
    }

    // In stateless mode (sessionIdGenerator: undefined), each request gets a new transport
    // but McpServer can only have one transport at a time. Close the existing connection
    // before connecting the new transport so Protocol._transport is reset to undefined.
    // The StreamableHTTPServerTransport with enableJsonResponse does not auto-close after
    // sending a JSON response, so we must close it explicitly before each new connection.
    try {
      await this.mcpServer.close()
    } catch (_e) {
      // Ignore close errors — server may not have been connected yet
    }

    const TransportClass = await getTransportClass()

    // Use stateless mode (sessionIdGenerator: undefined) to avoid requiring
    // Mcp-Session-Id header on every request. This allows clients to make
    // direct JSON-RPC calls without the full MCP initialization handshake.
    const transport = new TransportClass({
      sessionIdGenerator: undefined,
      enableJsonResponse: true,
    })

    // Set onclose BEFORE connect() so the SDK chains it with its own handler.
    // When the transport closes, both our cleanup and the SDK's cleanup run.
    transport.onclose = () => {
      this.sessions.delete(sessionId)
      this.requestContexts.delete(sessionId)
      this.config.onSessionDestroyed?.(sessionId)
      this.log?.(`Transport closed for session ${sessionId}`)
    }

    await this.mcpServer.connect(transport)

    // Force _initialized to true to allow requests without requiring initialize first.
    // This maintains backward compatibility with clients that don't send initialize.
    // The SDK sets _initialized = true only after processing an initialize request,
    // but we want to accept tool/resource/prompt calls directly.
    if (transport._initialized === false) {
      transport._initialized = true
    }

    this.sessions.set(sessionId, transport)

    this.log?.(`Created new transport for session ${sessionId}`)
    this.config.onSessionCreated?.(sessionId)

    return transport
  }

  /**
   * Destroy a session.
   */
  destroySession(sessionId: string): boolean {
    if (this.sessions.has(sessionId)) {
      const transport = this.sessions.get(sessionId)
      this.sessions.delete(sessionId)
      this.requestContexts.delete(sessionId) // Clean up request context
      this.log?.(`Destroyed session ${sessionId}`)
      this.config.onSessionDestroyed?.(sessionId)

      // Try to close the transport if it has a close method
      if (transport && typeof transport.close === 'function') {
        try {
          transport.close()
        } catch {
          // Ignore close errors
        }
      }

      return true
    }
    return false
  }

  /**
   * Get all active session IDs.
   */
  getActiveSessions(): string[] {
    return Array.from(this.sessions.keys())
  }

  /**
   * Get the number of active sessions.
   */
  getSessionCount(): number {
    return this.sessions.size
  }

  /**
   * Destroy all sessions.
   */
  destroyAllSessions(): void {
    const sessionIds = this.getActiveSessions()
    for (const sessionId of sessionIds) {
      this.destroySession(sessionId)
    }
    this.log?.(`Destroyed all ${sessionIds.length} sessions`)
  }

  /**
   * Internal logging helper (no-op if log not provided).
   */
  private log(message: string): void {
    this.config.log?.(message)
  }
}

/**
 * Create a new session manager.
 */
export function createSessionManager(config: SessionManagerConfig = {}): SessionManager {
  return new SessionManager(config)
}
